import React, { useEffect, useState } from 'react';
import { getDoctors, getBranches, getDepartments, createDoctor, updateDoctor, deleteDoctor, uploadDoctorPhoto, deleteDoctorPhoto } from '../api';
import styles from './Page.module.css';

const emptyForm = {
  name: '', specialization: '', qualification: '',
  experienceYears: '', consultationFee: '',
  branchIds: [], departmentId: '',
};

export default function DoctorsPage() {
  const [doctors, setDoctors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const BASE_URL = '';

  useEffect(() => {
    fetchDoctors();
    getBranches().then(r => setBranches(r.data));
    getDepartments().then(r => setDepartments(r.data));
  }, []);

  const fetchDoctors = () => getDoctors().then(r => setDoctors(r.data));

  const handleChange = e =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleBranchToggle = (id) => {
    const idStr = id.toString();
    const already = form.branchIds.includes(idStr);
    setForm({
      ...form,
      branchIds: already
        ? form.branchIds.filter(b => b !== idStr)
        : [...form.branchIds, idStr],
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleDeletePhoto = async () => {
    if (!editId) {
      setPhotoFile(null);
      setPhotoPreview(null);
      return;
    }
    if (!window.confirm("Delete this doctor's photo?")) return;
    try {
      await deleteDoctorPhoto(editId);
      setPhotoPreview(null);
      setPhotoFile(null);
      fetchDoctors();
      setMsg('Photo deleted.');
      setTimeout(() => setMsg(''), 3000);
    } catch {
      setMsg('Failed to delete photo.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.branchIds.length === 0) {
      setMsg('Please select at least one branch.');
      return;
    }
    setLoading(true);
    const payload = {
      name: form.name,
      specialization: form.specialization,
      qualification: form.qualification,
      experienceYears: parseInt(form.experienceYears) || 0,
      consultationFee: parseFloat(form.consultationFee) || 0,
      branches: form.branchIds.map(id => ({ id: parseInt(id) })),
      department: form.departmentId ? { id: parseInt(form.departmentId) } : null,
    };
    try {
      let savedId = editId;
      if (editId) {
        await updateDoctor(editId, payload);
        setMsg('Doctor updated successfully!');
      } else {
        const res = await createDoctor(payload);
        savedId = res.data.id;
        setMsg('Doctor added successfully!');
      }
      if (photoFile && savedId) {
        const formData = new FormData();
        formData.append('photo', photoFile);
        await uploadDoctorPhoto(savedId, formData);
      }
      setForm(emptyForm);
      setEditId(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      fetchDoctors();
      setTimeout(() => setMsg(''), 3000);
    } catch {
      setMsg('Operation failed.');
    }
    setLoading(false);
  };

  const handleEdit = (d) => {
    setEditId(d.id);
    setForm({
      name: d.name || '',
      specialization: d.specialization || '',
      qualification: d.qualification || '',
      experienceYears: d.experienceYears?.toString() || '',
      consultationFee: d.consultationFee?.toString() || '',
      branchIds: d.branches?.map(b => b.id.toString()) || [],
      departmentId: d.department?.id?.toString() || '',
    });
    setPhotoPreview(d.photo ? `${BASE_URL}${d.photo}` : null);
    setPhotoFile(null);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this doctor?')) return;
    try {
      await deleteDoctor(id);
      fetchDoctors();
    } catch {
      alert('Failed to delete doctor.');
    }
  };

  return (
    <div>
      <h2 className={styles.heading}>👨‍⚕️ Manage Doctors</h2>
      <div className={styles.grid}>
        <div className={styles.card}>
          <h3>{editId ? 'Edit Doctor' : 'Add New Doctor'}</h3>
          <form onSubmit={handleSubmit}>

            {/* Photo Upload */}
            <div className={styles.field}>
              <label>Doctor Photo</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {photoPreview ? (
                  <div style={{ position: 'relative' }}>
                    <img
                      src={photoPreview}
                      alt="Preview"
                      style={{
                        width: 80, height: 80,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '2px solid #D32F2F'
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleDeletePhoto}
                      style={{
                        position: 'absolute', top: -6, right: -6,
                        background: '#D32F2F', color: 'white',
                        border: 'none', borderRadius: '50%',
                        width: 22, height: 22, cursor: 'pointer',
                        fontSize: 12, lineHeight: '22px', textAlign: 'center'
                      }}
                      title="Remove photo"
                    >✕</button>
                  </div>
                ) : (
                  <div style={{
                    width: 80, height: 80, borderRadius: '50%',
                    backgroundColor: '#f0f0f0',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 32
                  }}>
                    👨‍⚕️
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  style={{ flex: 1 }}
                />
              </div>
            </div>

            <div className={styles.field}>
              <label>Full Name *</label>
              <input name="name" value={form.name}
                onChange={handleChange}
                placeholder="Dr. Rajesh Kumar" required />
            </div>
            <div className={styles.field}>
              <label>Specialization *</label>
              <input name="specialization" value={form.specialization}
                onChange={handleChange}
                placeholder="e.g. Cardiology" required />
            </div>
            <div className={styles.field}>
              <label>Qualification</label>
              <input name="qualification" value={form.qualification}
                onChange={handleChange}
                placeholder="e.g. MBBS, MD" />
            </div>
            <div className={styles.row}>
              <div className={styles.field}>
                <label>Experience (years)</label>
                <input name="experienceYears" value={form.experienceYears}
                  onChange={handleChange}
                  placeholder="10" type="number" />
              </div>
              <div className={styles.field}>
                <label>Consultation Fee (₹)</label>
                <input name="consultationFee" value={form.consultationFee}
                  onChange={handleChange}
                  placeholder="500" type="number" />
              </div>
            </div>

            <div className={styles.field}>
              <label>Department</label>
              <select name="departmentId" value={form.departmentId}
                onChange={handleChange}>
                <option value="">Select Department</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name} — {d.branch?.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.field}>
              <label>Branches * (select one or more)</label>
              <div className={styles.checkboxGrid}>
                {branches.map(b => (
                  <label key={b.id} className={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      checked={form.branchIds.includes(b.id.toString())}
                      onChange={() => handleBranchToggle(b.id)}
                    />
                    <span>{b.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {msg && <p className={styles.msg}>{msg}</p>}
            <div className={styles.btnRow}>
              <button type="submit" className={styles.btn} disabled={loading}>
                {loading ? 'Saving...' : editId ? 'Update Doctor' : 'Add Doctor'}
              </button>
              {editId && (
                <button type="button" className={styles.btnOutline}
                  onClick={() => {
                    setEditId(null);
                    setForm(emptyForm);
                    setPhotoFile(null);
                    setPhotoPreview(null);
                  }}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        <div className={styles.card}>
          <h3>All Doctors ({doctors.length})</h3>
          <div className={styles.list}>
            {doctors.map(d => (
              <div key={d.id} className={styles.item}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {d.photo ? (
                    <img
                      src={`${BASE_URL}${d.photo}`}
                      alt={d.name}
                      style={{
                        width: 48, height: 48,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '2px solid #D32F2F',
                        flexShrink: 0
                      }}
                    />
                  ) : (
                    <div style={{
                      width: 48, height: 48, borderRadius: '50%',
                      backgroundColor: '#f0f0f0',
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 22, flexShrink: 0
                    }}>
                      👨‍⚕️
                    </div>
                  )}
                  <div>
                    <strong>{d.name}</strong>
                    <span>{d.specialization} · ₹{d.consultationFee} · {d.experienceYears} yrs</span>
                    <span>{d.qualification}</span>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                      {d.branches?.map(b => (
                        <span key={b.id} className={styles.tag}>{b.name}</span>
                      ))}
                      {d.department && (
                        <span className={styles.tagBlue}>{d.department.name}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.actions}>
                  <button className={styles.editBtn}
                    onClick={() => handleEdit(d)}>✏️ Edit</button>
                  <button className={styles.deleteBtn}
                    onClick={() => handleDelete(d.id)}>🗑️ Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
